import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren, type ReactNode } from 'react'

import type { KeyCombo } from './comboUtils'

/**
 * A single page-context shortcut.
 *
 * `combo` is the structured combo (see `KeyCombo` in `comboUtils`) — e.g. `{ key: '1' }`
 * or `{ key: 'r', hasShift: true }`. `useKeyboardShortcuts` builds the same shape from
 * the keydown event and matches via `combosMatch`.
 *
 * `label` is the already-localized, human-readable description shown in the help overlay.
 *
 * `handler` is invoked by the global keydown handler when the combo matches and no
 * editable element is focused.
 *
 * `indicator` (optional) is rendered to the left of the label in the help overlay — used
 * e.g. for the colored dots that map a status filter shortcut to its connector chip color.
 */
export type PageShortcut = {
  combo: KeyCombo
  label: string
  handler: () => void
  indicator?: ReactNode
}

export type PageShortcutsRegistration = {
  /** Page name shown in the "ON {PAGE}" pill in the help-overlay header. Always required. */
  pageLabel: string
  /** Optional verbose heading shown above the page-context shortcut group. Defaults to `pageLabel`. */
  sectionLabel?: string
  /** Optional helper line under the section heading. */
  sectionSubtitle?: string
  shortcuts: PageShortcut[]
}

type ContextValue = {
  registration: PageShortcutsRegistration | null
  register: (registration: PageShortcutsRegistration) => () => void
}

const PageShortcutsContext = createContext<ContextValue>({
  registration: null,
  register: () => () => {}
})

/** Read the currently active page-shortcut registration (used by the help overlay + the keydown handler). */
export const usePageShortcutsRegistration = (): PageShortcutsRegistration | null => useContext(PageShortcutsContext).registration

export const PageShortcutsProvider = ({ children }: PropsWithChildren) => {
  const [registration, setRegistration] = useState<PageShortcutsRegistration | null>(null)

  const register = useCallback((next: PageShortcutsRegistration) => {
    setRegistration(next)
    return () => {
      // Only clear if the active registration is still ours — avoids a stale page
      // unmount wiping out a fresher registration that already mounted.
      setRegistration((current) => (current === next ? null : current))
    }
  }, [])

  const value = useMemo<ContextValue>(() => ({ registration, register }), [registration, register])
  return <PageShortcutsContext.Provider value={value}>{children}</PageShortcutsContext.Provider>
}

/**
 * Register page-scoped keyboard shortcuts for the lifetime of the calling component.
 *
 * Pass a stable `shortcuts` reference (e.g. from `useMemo`) — the registration re-runs
 * whenever the registration object changes by reference.
 *
 * Globals (`?`, `Esc`, `Cmd+K`, `N`, `G ...`, `.`, `Shift+D`) always take precedence;
 * page shortcuts only fire when no global matches.
 */
export const usePageShortcuts = (registration: PageShortcutsRegistration): void => {
  const { register } = useContext(PageShortcutsContext)
  useEffect(() => register(registration), [register, registration])
}
